/**
 * L'infobulle d'un point de courbe — **une seule, pour tous les graphiques**
 * (`ARCHITECTURE_V2.md` §6.3).
 *
 * *(05/09/2026, demandé après usage : « quand je clique sur un point de la
 * courbe, un petit tooltip s'affiche avec la valeur exacte du point ».)*
 *
 * 🔴 **C'est un revirement, et il faut savoir ce qu'il rouvre.** `WeightChart`
 * écartait nommément l'infobulle depuis l'étape 8 : « sur 393 px, une infobulle
 * près du doigt sort du cadre dès qu'on approche d'un bord et demanderait une
 * logique de recadrage ; une ligne fixe ne peut, par construction, jamais
 * déborder ». Le constat était juste — c'est la conclusion qui l'était moins :
 * une lecture posée **ailleurs** que sur le point oblige à faire le lien
 * soi-même, et sur un graphique c'est précisément le lien qu'on vient chercher.
 * On paie donc le recadrage, une fois, ici.
 *
 * 🔴 **Le recadrage vit dans `lib/chartScale.ts` (`tooltipAnchor`), pas ici** :
 * c'est de l'arithmétique pure, donc éprouvable au banc sans monter un DOM — et
 * c'est la seule chose de ce composant qui puisse être *fausse*. Le reste n'est
 * qu'un `<div>` et une classe.
 *
 * ⚠️ **`pointer-events: none`, et ce n'est pas cosmétique** : la bulle est
 * posée entre le doigt et le tracé. Sans cette ligne elle avalerait le
 * `pointermove` suivant, et le balayage s'arrêterait au premier point touché.
 *
 * ⚠️ **`aria-hidden`** : la valeur exacte est **déjà** lisible sans geste —
 * ligne de lecture au-dessus du tracé pour le poids, liste des passages sous la
 * courbe pour un exercice. C'est la règle du projet, « la couleur ne porte
 * jamais seule l'information » lue dans l'autre sens : l'infobulle est un
 * confort de pointage, pas la seule source.
 */

import type { CSSProperties, ReactNode } from 'react';

import { tooltipAnchor } from '../../lib/chartScale';

/** Écart entre le point et la bulle — assez pour que le doigt ne la couvre pas. */
const GAP = 10;

export function ChartTooltip({
  x,
  y,
  boxWidth,
  boxHeight,
  children,
}: {
  /** Abscisse du point, en pixels dans la boîte. */
  x: number;
  /** Ordonnée du point, en pixels dans la boîte. */
  y: number;
  boxWidth: number;
  boxHeight: number;
  children: ReactNode;
}) {
  const anchor = tooltipAnchor(x, y, boxWidth, boxHeight, GAP);

  const style: CSSProperties = {};
  if (anchor.left !== null) style.left = `${String(anchor.left)}px`;
  if (anchor.right !== null) style.right = `${String(anchor.right)}px`;
  if (anchor.top !== null) style.top = `${String(anchor.top)}px`;
  if (anchor.bottom !== null) style.bottom = `${String(anchor.bottom)}px`;
  if (anchor.centered) style.transform = 'translateX(-50%)';

  return (
    <div className="n-chart-tip tnum" style={style} aria-hidden>
      {children}
    </div>
  );
}
